import React, { useMemo } from "react"
import Box from "@mui/material/Box"
import IconButton from "@mui/material/IconButton"
import Typography from "@mui/material/Typography"
import Select from "@mui/material/Select"
import MenuItem from "@mui/material/MenuItem"
import KeyboardArrowLeftOutlinedIcon from "@mui/icons-material/KeyboardArrowLeftOutlined"
import KeyboardArrowRightOutlinedIcon from "@mui/icons-material/KeyboardArrowRightOutlined"

export const Pagination = ({
  currentPage,
  totalItems,
  perPage,
  onPageChange,
  onPerPageChange,
  perPageOptions = [10, 20, 50],
}) => {
  const totalPages = Math.max(1, Math.ceil(totalItems / perPage))

  const paginationItems = useMemo(() => {
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, index) => index + 1)
    }

    if (currentPage <= 3) {
      return [1, 2, 3, "...", totalPages]
    }

    if (currentPage >= totalPages - 2) {
      return [1, "...", totalPages - 2, totalPages - 1, totalPages]
    }

    return [
      1,
      "...",
      currentPage - 1,
      currentPage,
      currentPage + 1,
      "...",
      totalPages,
    ]
  }, [currentPage, totalPages])

  const handlePageChange = (page) => {
    if (page === "..." || page === currentPage) {
      return
    }

    onPageChange(page)
  }

  const handlePrevPage = () => {
    onPageChange(Math.max(1, currentPage - 1))
  }

  const handleNextPage = () => {
    onPageChange(Math.min(totalPages, currentPage + 1))
  }

  const handlePerPageChange = (event) => {
    onPerPageChange(Number(event.target.value))
    onPageChange(1)
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: {
          xs: "column",
          md: "row",
        },
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        position: "relative",
        pt: 2.6,
        pb: 0.4,
      }}
    >
      <Box sx={{ display: "flex", gap: 1 }}>
        {["prev", ...paginationItems, "next"].map((item, index) => {
          const isActive = item === currentPage
          const isPrevDisabled = item === "prev" && currentPage === 1
          const isNextDisabled = item === "next" && currentPage === totalPages
          const isDisabled = item === "..." || isPrevDisabled || isNextDisabled

          return (
            <IconButton
              key={`${item}-${index}`}
              disabled={isDisabled}
              onClick={() => {
                if (item === "prev") {
                  handlePrevPage()
                  return
                }

                if (item === "next") {
                  handleNextPage()
                  return
                }

                handlePageChange(item)
              }}
              sx={{
                width: 40,
                height: 40,
                borderRadius: "4px",
                color: isActive ? "#c49654" : "#dedede",
                border: isActive
                  ? "1px solid #b8861d"
                  : "1px solid rgba(255,255,255,0.12)",
                backgroundColor: isActive
                  ? "rgba(184,134,29,0.08)"
                  : "transparent",
                opacity: isDisabled ? 0.45 : 1,
                "&:hover": {
                  borderColor: "#b8861d",
                },
              }}
            >
              {item === "prev" && <KeyboardArrowLeftOutlinedIcon />}
              {item === "next" && <KeyboardArrowRightOutlinedIcon />}
              {item !== "prev" && item !== "next" && (
                <Typography sx={{ fontWeight: 900 }}>{item}</Typography>
              )}
            </IconButton>
          )
        })}
      </Box>

      <Box
        sx={{
          position: {
            xs: "static",
            md: "absolute",
          },
          right: 0,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          color: "#999",
        }}
      >
        Show
        <Select
          size="small"
          value={perPage}
          onChange={handlePerPageChange}
          sx={{
            width: 72,
            color: "#fff",
            ".MuiOutlinedInput-notchedOutline": {
              borderColor: "rgba(255,255,255,0.12)",
            },
            ".MuiSvgIcon-root": {
              color: "#fff",
            },
          }}
        >
          {perPageOptions.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
        per page
      </Box>
    </Box>
  )
}
